import { Platform } from "react-native";
import { create } from "zustand";
import { persist, createJSONStorage, type StateStorage } from "zustand/middleware";
import AsyncStorage from "@react-native-async-storage/async-storage";

/** Which viewport class a collapse preference belongs to. */
export type SidebarViewport = "narrow" | "wide";

/**
 * Collapse is remembered per viewport class rather than as one flag. A narrow
 * window wants the icon rail by default (the full sidebar leaves too little
 * room beside it), but the same user on a wide monitor wants their expanded
 * sidebar back — one shared flag means whichever they touched last wins and
 * the other viewport silently inherits it.
 */
type CollapsedByViewport = Record<SidebarViewport, boolean>;

interface SidebarState {
  collapsed: CollapsedByViewport;
  expandedItemIds: readonly string[];
  toggleCollapsed: (viewport: SidebarViewport) => void;
  setExpandedItemIds: (ids: readonly string[]) => void;
}

const defaultCollapsed: CollapsedByViewport = { narrow: true, wide: false };

const webLocalStorage: StateStorage = {
  getItem: (name) => {
    try {
      return window.localStorage.getItem(name);
    } catch {
      return null;
    }
  },
  setItem: (name, value) => {
    try {
      window.localStorage.setItem(name, value);
    } catch {
      // localStorage may be unavailable
    }
  },
  removeItem: (name) => {
    try {
      window.localStorage.removeItem(name);
    } catch {
      // localStorage may be unavailable
    }
  },
};

/**
 * Reads the stored collapse preference, tolerating the legacy shape: this used
 * to be a single boolean, which we treat as the wide-viewport preference.
 */
function readCollapsed(stored: unknown, fallback: CollapsedByViewport): CollapsedByViewport {
  if (typeof stored === "boolean") return { ...fallback, wide: stored };
  if (typeof stored !== "object" || stored === null) return fallback;
  const byViewport = stored as Partial<CollapsedByViewport>;
  return {
    narrow: typeof byViewport.narrow === "boolean" ? byViewport.narrow : fallback.narrow,
    wide: typeof byViewport.wide === "boolean" ? byViewport.wide : fallback.wide,
  };
}

export const useSidebarStore = create<SidebarState>()(
  persist(
    (set) => ({
      collapsed: defaultCollapsed,
      expandedItemIds: [],
      toggleCollapsed: (viewport) =>
        set((state) => ({ collapsed: { ...state.collapsed, [viewport]: !state.collapsed[viewport] } })),
      setExpandedItemIds: (ids) => set({ expandedItemIds: ids }),
    }),
    {
      name: "@web_sidebar",
      storage: createJSONStorage(() => (Platform.OS === "web" ? webLocalStorage : AsyncStorage)),
      merge: (persisted, current) => {
        const stored = persisted as Partial<SidebarState> | null;
        return {
          ...current,
          collapsed: readCollapsed(stored?.collapsed, current.collapsed),
          expandedItemIds:
            (
              Array.isArray(stored?.expandedItemIds) &&
              stored.expandedItemIds.every((v: unknown) => typeof v === "string")
            ) ?
              stored.expandedItemIds
            : current.expandedItemIds,
        };
      },
    },
  ),
);
